import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { listContactsByAgent } from '../../api/contacts';
import AgentSearchSelect from '../../components/form/AgentSearchSelect';
import { getApiErrorMessage } from '../../utils/errors';

const PER_PAGE = 20;
const CONTACT_FILTER_DEBOUNCE_MS = 300;

const STATUS_LABELS = {
  active: 'Ativo',
  blocked: 'Bloqueado',
  paused: 'Pausado',
};

function formatDate(iso) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(iso));
}

function buildMetaLine(contact) {
  const parts = [
    contact.onboarding_completed ? 'Onboarding ok' : 'Onboarding pendente',
  ];

  const date = formatDate(contact.updated_at);
  if (date) {
    parts.push(`Atualizado ${date}`);
  }

  return parts.join(' · ');
}

function StatusBadge({ status }) {
  const styles = {
    active: 'text-emerald-700 bg-emerald-50',
    blocked: 'text-red-700 bg-red-50',
    paused: 'text-amber-700 bg-amber-50',
  };

  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
        styles[status] || 'text-content-subtle bg-slate-100'
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ContactRow({ contact, agentId }) {
  const meta = buildMetaLine(contact);
  const detailPath = agentId
    ? `/contacts/${contact.id}?agentId=${agentId}`
    : `/contacts/${contact.id}`;

  return (
    <li className="surface-card px-4 py-3.5 hover:bg-slate-50/60 transition-colors">
      <Link to={detailPath} className="block min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <p className="font-medium text-content-primary truncate">{contact.external_id}</p>
          <StatusBadge status={contact.status} />
        </div>
        {meta && (
          <p className="text-xs text-content-muted mt-1 truncate" title={meta}>
            {meta}
          </p>
        )}
      </Link>
    </li>
  );
}

function ContactsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialAgentId = searchParams.get('agentId') || '';

  const [contacts, setContacts] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState(initialAgentId);
  const [contactFilter, setContactFilter] = useState('');
  const [debouncedContactFilter, setDebouncedContactFilter] = useState('');
  const [meta, setMeta] = useState({ page: 1, per_page: PER_PAGE, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactFilter(contactFilter.trim());
    }, CONTACT_FILTER_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [contactFilter]);

  useEffect(() => {
    const agentIdFromUrl = searchParams.get('agentId') || '';
    if (agentIdFromUrl !== selectedAgentId) {
      setSelectedAgentId(agentIdFromUrl);
    }
  }, [searchParams, selectedAgentId]);

  const syncAgentToUrl = useCallback(
    (agentId) => {
      const next = new URLSearchParams(searchParams);
      if (agentId) {
        next.set('agentId', agentId);
      } else {
        next.delete('agentId');
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const loadContacts = useCallback(
    async (page = 1) => {
      if (!selectedAgentId) {
        setContacts([]);
        setMeta({ page: 1, per_page: PER_PAGE, total: 0 });
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await listContactsByAgent(selectedAgentId, {
          page,
          perPage: PER_PAGE,
          search: debouncedContactFilter || null,
        });
        setContacts(response.data || []);
        setMeta(response.meta || { page, per_page: PER_PAGE, total: 0 });
      } catch (err) {
        setError(getApiErrorMessage(err, 'Não foi possível carregar os contatos.'));
        setContacts([]);
      } finally {
        setLoading(false);
      }
    },
    [selectedAgentId, debouncedContactFilter]
  );

  useEffect(() => {
    loadContacts(1);
  }, [loadContacts]);

  const handleAgentFilterChange = (event) => {
    const agentId = event.target.value;
    setSelectedAgentId(agentId);
    syncAgentToUrl(agentId);
  };

  const handleContactFilterChange = (event) => {
    setContactFilter(event.target.value);
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.per_page));
  const hasPagination = meta.total > meta.per_page;
  const awaitingAgent = !selectedAgentId;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-content-primary">Contatos</h1>
          <p className="text-content-muted text-sm mt-1">
            Contatos que interagiram com seus agentes
          </p>
        </div>
        {!loading && !awaitingAgent && meta.total > 0 && (
          <p className="text-sm text-content-subtle">
            {meta.total} contato{meta.total !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      <div className="flex flex-col lg:flex-row lg:items-end gap-3">
        <AgentSearchSelect
          id="agent-filter"
          value={selectedAgentId}
          onChange={handleAgentFilterChange}
          mode="user"
          showAllOption={false}
          disabled={loading}
        />

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 min-w-0 flex-1">
          <label htmlFor="contact-filter" className="text-sm text-content-muted shrink-0">
            Contato
          </label>
          <input
            id="contact-filter"
            type="search"
            value={contactFilter}
            onChange={handleContactFilterChange}
            disabled={loading || awaitingAgent}
            placeholder="Buscar por telefone..."
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-content-primary min-w-0 w-full sm:max-w-xs"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-red-700">{error}</p>
          <button
            type="button"
            onClick={() => loadContacts(meta.page)}
            className="btn-secondary text-sm px-3 py-2"
          >
            Tentar novamente
          </button>
        </div>
      )}

      {awaitingAgent ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
          <p className="text-content-primary font-medium">Selecione um agente</p>
          <p className="text-content-muted text-sm mt-2">
            Escolha um agente acima para ver os contatos vinculados a ele.
          </p>
        </div>
      ) : loading ? (
        <div className="surface-card px-4 py-12 text-center">
          <p className="text-content-muted text-sm">Carregando contatos...</p>
        </div>
      ) : contacts.length === 0 && !error ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-12 text-center">
          <p className="text-content-primary font-medium">Nenhum contato encontrado</p>
          <p className="text-content-muted text-sm mt-2">
            {debouncedContactFilter
              ? 'Nenhum contato corresponde à busca.'
              : 'Este agente ainda não possui contatos.'}
          </p>
        </div>
      ) : (
        <>
          <ul className="space-y-2">
            {contacts.map((contact) => (
              <ContactRow key={contact.id} contact={contact} agentId={selectedAgentId} />
            ))}
          </ul>

          {hasPagination && (
            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-content-subtle">
                Página {meta.page} de {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={meta.page <= 1 || loading}
                  onClick={() => loadContacts(meta.page - 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  disabled={meta.page >= totalPages || loading}
                  onClick={() => loadContacts(meta.page + 1)}
                  className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                >
                  Próxima
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ContactsList;
